/**
 * ProjectDetailOverlay — Full project details
 * 
 * v4.7 — Fix mention dropdown closing overlay on click:
 *   - handleClickOutside now ignores clicks inside role="listbox" elements
 *     (MentionTextarea autocomplete dropdown) so picking a project suggestion
 *     no longer closes the overlay before the token can be inserted.
 * 
 * v4.6 — Description expand + CollectionCard refactor:
 *   - Collection card descriptions now expand/collapse inline
 *   - "Show more / Show less" toggle on descriptions > 120 chars
 *   - Extracted CollectionCard sub-component (own state per card)
 *   - Expand tap does NOT navigate to the collection (stopPropagation)
 * 
 * v4.5 — Three visual upgrades:
 * 
 *   LineageTree (v4.3):
 *   - Minimap: colored dot rail showing full chain at a glance
 *   - Jump pills: contextual shortcuts (Origin, This Project, zone crosses, Latest)
 *   - Card viewport: 3-visible sliding window with smooth transitions
 *   - Arrow key navigation + click any minimap dot to jump
 *   - Cross-zone transitions celebrated with fuchsia flash + badge
 *   - Short chains (≤4 projects) still render as simple horizontal row
 * 
 *   Collections (v4.4):
 *   - Rich collection cards with curator avatar, description, badges
 *   - Featured/Public/Private badge system with gradient accent
 *   - "Rooted from" indicator, view count, project count
 *   - 2-line clamped description preview
 * 
 *   Transitions & polish (v4.5):
 *   - Content crossfade when navigating between projects via lineage/collections
 *   - Auto scroll-to-top on project navigation
 *   - Zone-color glow smoothly transitions when changing projects
 *   - Transient state (delete confirm, citations, purchase) resets on nav
 *   - Press-scale (active:scale-[0.98]) on all tappable cards and rows
 * 
 * Place at: src/components/overlays/ProjectDetailOverlay.jsx
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, GitFork, Target, Heart, Eye, Download, Share2, Edit3, Trash2,
  Calendar, User, Tag, MapPin, ExternalLink, MessageCircle, Clock,
  Package, Monitor, Layers, ChevronRight, ChevronDown, ChevronLeft, Copy, BookOpen,
  CheckCircle2, FolderTree, Globe, Lock, ShoppingBag, DollarSign,
  FileText, History, Timer, Activity, Sparkles, Zap, Radio, Users
} from 'lucide-react';
import { cn, formatRelativeTime, formatDate, formatNumber, parseTags, copyToClipboard, formatFileSize } from '../../utils/cn';
import { Button, Badge, Avatar, Card } from '../ui';
import { PROJECT_TYPES, getProjectZone } from '../../config/zones';
import { useAuth } from '../../context/AuthContext';
import PurchaseOverlay from './PurchaseOverlay';
import VersionHistory from './VersionHistory';
import { DeadlineTab } from './DeadlineManager';
import { api } from '../../api';
import { API_ORIGIN } from '../../config/apiBase';

// ── Reserved physical-item tags (must match CreateProjectOverlay) ──
const PHYSICAL_TAGS = {
  availability: { label: 'Availability', color: 'emerald', tags: ['for-sale', 'for-trade', 'to-borrow', 'free', 'pickup-only', 'will-ship'] },
  condition:    { label: 'Condition',    color: 'amber',   tags: ['new', 'like-new', 'good', 'fair', 'worn'] },
  craft:        { label: 'Craft',        color: 'violet',  tags: ['handmade', 'one-of-a-kind', 'prototype', 'sample', 'limited-run', 'final'] },
};
const ALL_PHYSICAL_TAGS = Object.values(PHYSICAL_TAGS).flatMap(g => g.tags);

function getPhysicalTagStyle(tag) {
  for (const group of Object.values(PHYSICAL_TAGS)) {
    if (group.tags.includes(tag)) {
      if (group.color === 'emerald') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
      if (group.color === 'amber')   return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
      if (group.color === 'violet')  return 'bg-violet-500/15 text-violet-400 border-violet-500/30';
    }
  }
  return null;
}

const AVAILABILITY_LABELS = {
  'for-sale': 'For sale',
  'for-trade': 'Open to trades',
  'to-borrow': 'Available to borrow',
  'free': 'Free',
  'pickup-only': 'Pickup only',
  'will-ship': 'Will ship',
};
import { CitationGenerator } from '../citations/CitationGenerator';
import UsedInPanel from './UsedInPanel';
import ConnectionsPanel, { useProjectConnections, MentionText } from './ConnectionsPanel';


// ============================================================================
// HELPERS
// ============================================================================

function buildDownloadUrl(url) {
  if (!url) return null;
  // See note in VersionHistory — stored paths join to the origin, not API_BASE.
  const fullUrl = url.startsWith('http') ? url : API_ORIGIN + url;
  return fullUrl + (fullUrl.includes('?') ? '&' : '?') + 'download=true';
}

function triggerDownload(url, filename) {
  const fullUrl = buildDownloadUrl(url);
  if (!fullUrl) return;
  const a = document.createElement('a');
  a.href = fullUrl;
  a.download = filename || 'download';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function collectProjectFiles(project) {
  return [
    ...(project.digital_items || []).map(item => ({
      ...item, name: item.name,
      downloadUrl: item.file_url || item.fileUrl,
      fileType: item.file_type || item.fileType || 'digital',
      fileSize: item.file_size || item.fileSize,
    })),
    ...(project.physical_items || []).map(item => ({
      ...item, name: item.name,
      downloadUrl: item.image_url || item.imageUrl,
      fileType: 'physical',
    })),
    ...(project.files || []).map(file => ({
      ...file, name: file.name || file.file_name,
      downloadUrl: file.file_url || file.fileUrl || file.url,
      fileType: file.file_type || file.type || 'File',
      fileSize: file.file_size || file.fileSize,
    })),
  ];
}

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  if (s < 2592000) return `${Math.floor(s / 604800)}w ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
};


// ============================================================================
// INLINE FILE ROW
// ============================================================================

const FileRow = ({ file }) => (
  <button
    onClick={() => triggerDownload(file.downloadUrl, file.name)}
    className="flex items-center gap-3 w-full p-2.5 rounded-xl bg-zinc-800/40 hover:bg-zinc-800/70
               border border-zinc-800/40 hover:border-zinc-700/50
               transition-all active:scale-[0.98] group text-left"
  >
    <div className="w-8 h-8 rounded-lg bg-zinc-700/30 flex items-center justify-center flex-shrink-0">
      <FileText className="w-3.5 h-3.5 text-zinc-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[12px] font-medium text-zinc-300 truncate group-hover:text-white transition-colors">
        {file.name || 'Untitled file'}
        {file.isLatest && (
          <span className="ml-1.5 text-[9px] px-1.5 py-0.5 bg-purple-500/15 text-purple-400 rounded font-semibold">
            LATEST
          </span>
        )}
      </p>
      <p className="text-[10px] text-zinc-600">
        {file.fileType}{file.fileSize ? ` \u2022 ${formatFileSize(file.fileSize)}` : ''}
      </p>
    </div>
    <Download className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
  </button>
);


// ============================================================================
// LINEAGE TREE — Adaptive lineage viewer
//
// Short chains (≤ 4 projects): simple horizontal row (original behavior)
// Long chains (5+ projects):   minimap + sliding viewport + jump pills
//
// Same props as before — drop-in replacement.
// ============================================================================

const ZONE_COLORS = {
  build: '#f59e0b', beautiful: '#ec4899', sound: '#06b6d4', taste: '#fb923c',
  story: '#8b5cf6', heal: '#ef4444', smart: '#6366f1', sell: '#22c55e', default: '#71717a',
};

const ZONE_LABELS = {
  build: 'Build', beautiful: 'Beautiful', sound: 'Sound', taste: 'Taste',
  story: 'Story', heal: 'Heal', smart: 'Smart', sell: 'Sell', default: '?',
};

const getZoneColor = (project) => {
  try {
    const z = getProjectZone(project);
    return { id: z.id, color: z.color, label: z.shortLabel };
  } catch {
    return { id: 'default', color: '#71717a', label: '?' };
  }
};

// ── Small card used in both simple and viewport modes ──
const LineageTreeCard = ({ project, isCurrent, isActive, onClick, showCrossFlash }) => {
  const zone = getZoneColor(project);
  const img = project?.image_url || project?.imageUrl || project?.cover_image || project?.coverImage;

  return (
    <button
      onClick={() => onClick?.(project)}
      className={cn(
        'flex items-center gap-2.5 p-2.5 rounded-xl text-left transition-all active:scale-[0.98] group flex-shrink-0 relative',
        isCurrent
          ? 'border-2 border-purple-500/50 bg-purple-500/10'
          : isActive
            ? 'border border-zinc-700 bg-zinc-800/70'
            : 'border border-zinc-800 bg-zinc-900 hover:border-zinc-700 hover:bg-zinc-800/50',
      )}
      style={{ minWidth: isCurrent ? 200 : 180, maxWidth: 220 }}
    >
      {/* Cross-zone flash indicator */}
      {showCrossFlash && (
        <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-fuchsia-500" />
      )}
      <div
        className={cn(
          'rounded-lg overflow-hidden flex-shrink-0',
          isCurrent ? 'w-11 h-11 border border-purple-500/30' : 'w-10 h-10 border',
        )}
        style={{ borderColor: isCurrent ? undefined : `${zone.color}30` }}
      >
        {img ? (
          <img src={img} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center font-bold text-xs"
            style={{ backgroundColor: `${zone.color}15`, color: zone.color }}
          >
            {project?.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn(
          'text-[12px] font-medium truncate transition-colors',
          isCurrent ? 'text-white font-semibold' : 'text-zinc-300 group-hover:text-purple-300',
        )}>
          {project?.name}
        </p>
        <p className="text-[10px] text-zinc-500 truncate">
          {project?.author?.username ? `@${project.author.username}` : ''}
        </p>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />
          <span className="text-[9px] font-medium" style={{ color: zone.color }}>{zone.label}</span>
        </div>
      </div>
    </button>
  );
};

const LineageForkDivider = ({ crossZone, fromZone, toZone }) => (
  <div className="flex flex-col items-center justify-center px-1 flex-shrink-0" style={{ minWidth: 28 }}>
    {crossZone && fromZone && toZone && (
      <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-fuchsia-500/8 border border-fuchsia-500/15 mb-1">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ZONE_COLORS[fromZone] || ZONE_COLORS.default }} />
        <Activity className="w-2 h-2 text-fuchsia-400" />
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ZONE_COLORS[toZone] || ZONE_COLORS.default }} />
      </div>
    )}
    <div className="flex items-center text-zinc-600">
      <div className="h-px w-2.5 bg-zinc-700" />
      <GitFork className="w-3 h-3 -rotate-90" />
      <div className="h-px w-2.5 bg-zinc-700" />
    </div>
  </div>
);


// ── Minimap: colored dot rail ──
const LineageMinimap = ({ chain, zones, activeIdx, currentIdx, onDotClick }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const dots = container.querySelectorAll('[data-dot]');
    const activeDot = dots[activeIdx];
    if (activeDot) {
      activeDot.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeIdx]);

  return (
    <div
      ref={scrollRef}
      className="flex items-center gap-[3px] px-3 py-2.5 overflow-x-auto"
      style={{ scrollbarWidth: 'none' }}
    >
      {chain.map((project, i) => {
        const zoneColor = ZONE_COLORS[zones[i]] || ZONE_COLORS.default;
        const isCross = i > 0 && zones[i] !== zones[i - 1];
        const isActive = i === activeIdx;
        const isCurrent = i === currentIdx;

        return (
          <React.Fragment key={project.id}>
            {i > 0 && (
              <div
                className="flex-shrink-0"
                style={{
                  width: isCross ? 10 : 6,
                  height: isCross ? 2 : 1,
                  backgroundColor: isCross ? '#d946ef' : '#3f3f46',
                  borderRadius: 1,
                }}
              />
            )}
            <button
              data-dot
              onClick={() => onDotClick(i)}
              className="flex-shrink-0 rounded-full transition-all duration-200"
              title={project.name}
              style={{
                width: isActive ? 12 : isCurrent ? 10 : 8,
                height: isActive ? 12 : isCurrent ? 10 : 8,
                backgroundColor: zoneColor,
                border: isCurrent ? '2px solid #a855f7' : isActive ? `2px solid ${zoneColor}` : '1.5px solid transparent',
                boxShadow: isActive ? `0 0 0 2px #18181b, 0 0 0 3.5px ${zoneColor}` : 'none',
                transform: isActive ? 'scale(1.15)' : 'scale(1)',
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};


// ── Jump pills: contextual navigation shortcuts ──
const LineageJumpPills = ({ chain, zones, currentIdx, activeIdx, onJump }) => {
  // Collect zone-cross indices
  const crosses = useMemo(() => {
    const result = [];
    for (let i = 1; i < zones.length; i++) {
      if (zones[i] !== zones[i - 1]) {
        result.push({ idx: i, from: zones[i - 1], to: zones[i] });
      }
    }
    return result;
  }, [zones]);

  return (
    <div className="flex items-center gap-1.5 px-3 pb-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
      {/* Origin */}
      <button
        onClick={() => onJump(0)}
        className={cn(
          'flex-shrink-0 text-[10px] font-medium px-2.5 py-1 rounded-full border transition-all',
          activeIdx === 0
            ? 'bg-zinc-700/50 border-zinc-600 text-zinc-200'
            : 'bg-zinc-800/40 border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700',
        )}
      >
        Origin
      </button>

      {/* Current project pill */}
      {currentIdx > 0 && currentIdx < chain.length - 1 && (
        <button
          onClick={() => onJump(currentIdx)}
          className={cn(
            'flex-shrink-0 text-[10px] font-medium px-2.5 py-1 rounded-full border transition-all',
            activeIdx === currentIdx
              ? 'bg-purple-500/15 border-purple-500/40 text-purple-300'
              : 'bg-zinc-800/40 border-purple-500/20 text-purple-400/70 hover:text-purple-300 hover:border-purple-500/40',
          )}
        >
          This project
        </button>
      )}

      {/* Zone-cross pills (show up to 3 to save space) */}
      {crosses.slice(0, 3).map((cross) => (
        <button
          key={cross.idx}
          onClick={() => onJump(cross.idx)}
          className={cn(
            'flex-shrink-0 flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-full border transition-all',
            activeIdx === cross.idx
              ? 'bg-fuchsia-500/10 border-fuchsia-500/30 text-fuchsia-300'
              : 'bg-zinc-800/40 border-fuchsia-500/15 text-fuchsia-400/60 hover:text-fuchsia-300 hover:border-fuchsia-500/30',
          )}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ZONE_COLORS[cross.from] || ZONE_COLORS.default }} />
          <span>{ZONE_LABELS[cross.from] || '?'}</span>
          <ChevronRight className="w-2.5 h-2.5 opacity-60" />
          <span>{ZONE_LABELS[cross.to] || '?'}</span>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ZONE_COLORS[cross.to] || ZONE_COLORS.default }} />
        </button>
      ))}

      {/* Latest */}
      <button
        onClick={() => onJump(chain.length - 1)}
        className={cn(
          'flex-shrink-0 text-[10px] font-medium px-2.5 py-1 rounded-full border transition-all',
          activeIdx === chain.length - 1
            ? 'bg-zinc-700/50 border-zinc-600 text-zinc-200'
            : 'bg-zinc-800/40 border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700',
        )}
      >
        Latest
      </button>
    </div>
  );
};


// ── Main LineageTree: adaptive short/long rendering ──
const LineageTree = ({ parentProjects, childProjects, currentProject, onViewProject }) => {
  const chain = useMemo(
    () => {
      const all = [...parentProjects, currentProject, ...childProjects].filter(Boolean);
      // Deduplicate by id (a project could appear in both parents and children
      // in multi-parent fork scenarios)
      const seen = new Set();
      const unique = all.filter(p => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
      // Sort chronologically — oldest ancestor first, newest descendant last
      unique.sort((a, b) => {
        const aDate = a.created_at || a.createdAt || '';
        const bDate = b.created_at || b.createdAt || '';
        if (aDate && bDate) return new Date(aDate) - new Date(bDate);
        return a.id - b.id; // fallback: lower ID = older
      });
      return unique;
    },
    [parentProjects, childProjects, currentProject],
  );

  if (chain.length <= 1) return null;

  const zones = useMemo(() => chain.map(p => getZoneColor(p).id), [chain]);
  const currentIdx = chain.findIndex(p => p.id === currentProject?.id);

  const crossCount = useMemo(() => {
    let count = 0;
    for (let i = 1; i < zones.length; i++) {
      if (zones[i] !== zones[i - 1]) count++;
    }
    return count;
  }, [zones]);

  // For short chains, use the original simple layout
  const isShortChain = chain.length <= 4;

  // Active index state for long-chain viewport mode
  const [activeIdx, setActiveIdx] = useState(currentIdx >= 0 ? currentIdx : 0);
  const trackRef = useRef(null);

  // Keyboard navigation for the viewport
  useEffect(() => {
    if (isShortChain) return;

    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIdx(prev => Math.max(0, prev - 1));
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIdx(prev => Math.min(chain.length - 1, prev + 1));
      }
    };

    // Only attach if this component's rail is focused/hovered
    const rail = trackRef.current?.closest('[data-lineage-rail]');
    if (!rail) return;

    const onFocus = () => document.addEventListener('keydown', handleKey);
    const onBlur = () => document.removeEventListener('keydown', handleKey);

    rail.addEventListener('mouseenter', onFocus);
    rail.addEventListener('mouseleave', onBlur);
    rail.addEventListener('focusin', onFocus);
    rail.addEventListener('focusout', onBlur);

    return () => {
      document.removeEventListener('keydown', handleKey);
      rail.removeEventListener('mouseenter', onFocus);
      rail.removeEventListener('mouseleave', onBlur);
      rail.removeEventListener('focusin', onFocus);
      rail.removeEventListener('focusout', onBlur);
    };
  }, [isShortChain, chain.length]);

  // How many cards visible in the viewport (responsive)
  const VISIBLE_CARDS = 3;
  const CARD_WIDTH = 200; // approximate width per card slot

  // Compute track offset to keep activeIdx centered (or near-centered)
  const trackOffset = useMemo(() => {
    const maxOffset = Math.max(0, chain.length - VISIBLE_CARDS);
    const idealOffset = activeIdx - Math.floor(VISIBLE_CARDS / 2);
    return Math.max(0, Math.min(idealOffset, maxOffset));
  }, [activeIdx, chain.length]);

  const handleJump = useCallback((idx) => {
    setActiveIdx(Math.max(0, Math.min(idx, chain.length - 1)));
  }, [chain.length]);

  // ── Header (shared) ──
  const header = (
    <h3 className="text-[13px] font-semibold text-zinc-300 mb-2.5 flex items-center gap-2">
      <GitFork className="w-3.5 h-3.5 text-purple-400" />
      Creative Lineage
      <span className="text-[10px] text-zinc-600 font-normal">
        {chain.length} project{chain.length !== 1 ? 's' : ''}
        {crossCount > 0 && (
          <span className="text-fuchsia-400/70 ml-1">
            · {crossCount} zone cross{crossCount !== 1 ? 'es' : ''}
          </span>
        )}
      </span>
    </h3>
  );

  // ── SHORT CHAIN: simple horizontal row (original behavior) ──
  if (isShortChain) {
    return (
      <div className="mb-6">
        {header}
        <div
          className="overflow-x-auto pb-2 -mx-1 px-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#27272a transparent' }}
        >
          <div className="flex items-center gap-0 min-w-max">
            {chain.map((project, idx) => {
              const isCurrent = project.id === currentProject?.id;
              const prevZone = idx > 0 ? zones[idx - 1] : null;
              const currZone = zones[idx];
              const isCrossZone = idx > 0 && prevZone !== currZone;

              return (
                <React.Fragment key={project.id}>
                  {idx > 0 && (
                    <LineageForkDivider
                      crossZone={isCrossZone}
                      fromZone={prevZone}
                      toZone={currZone}
                    />
                  )}
                  <LineageTreeCard
                    project={project}
                    isCurrent={isCurrent}
                    onClick={onViewProject}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── LONG CHAIN: minimap + viewport + jump pills ──
  return (
    <div className="mb-6">
      {header}

      <div
        data-lineage-rail
        className="bg-zinc-800/30 border border-zinc-800/50 rounded-xl overflow-hidden"
        tabIndex={0}
      >
        {/* Minimap */}
        <div className="border-b border-zinc-800/40">
          <LineageMinimap
            chain={chain}
            zones={zones}
            activeIdx={activeIdx}
            currentIdx={currentIdx}
            onDotClick={handleJump}
          />
        </div>

        {/* Jump pills */}
        <div className="border-b border-zinc-800/40 py-1.5">
          <LineageJumpPills
            chain={chain}
            zones={zones}
            currentIdx={currentIdx}
            activeIdx={activeIdx}
            onJump={handleJump}
          />
        </div>

        {/* Card viewport */}
        <div className="relative overflow-hidden" style={{ height: 88 }}>
          <div
            ref={trackRef}
            className="flex items-stretch absolute top-0 left-0 h-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${trackOffset * CARD_WIDTH}px)` }}
          >
            {chain.map((project, idx) => {
              const isCurrent = project.id === currentProject?.id;
              const isActive = idx === activeIdx;
              const isCrossZone = idx > 0 && zones[idx] !== zones[idx - 1];
              const zone = getZoneColor(project);
              const img = project?.image_url || project?.imageUrl || project?.cover_image || project?.coverImage;

              return (
                <div
                  key={project.id}
                  className="flex-shrink-0 relative"
                  style={{ width: CARD_WIDTH }}
                >
                  {/* Cross-zone flash on left edge */}
                  {isCrossZone && (
                    <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-fuchsia-500 z-10" />
                  )}
                  <button
                    onClick={() => {
                      setActiveIdx(idx);
                      onViewProject?.(project);
                    }}
                    className={cn(
                      'flex items-center gap-3 w-full h-full px-3 py-2.5 text-left transition-all group',
                      isActive
                        ? 'bg-zinc-800/60'
                        : 'hover:bg-zinc-800/30',
                      isCurrent && 'ring-1 ring-inset ring-purple-500/40',
                    )}
                    style={{
                      borderRight: '1px solid rgba(63,63,70,0.3)',
                    }}
                  >
                    {/* Thumbnail */}
                    <div
                      className={cn(
                        'rounded-lg overflow-hidden flex-shrink-0 transition-transform duration-200',
                        isActive ? 'w-11 h-11 scale-105' : 'w-10 h-10',
                      )}
                      style={{ border: `1.5px solid ${zone.color}30` }}
                    >
                      {img ? (
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center font-bold text-xs"
                          style={{ backgroundColor: `${zone.color}15`, color: zone.color }}
                        >
                          {project?.name?.[0]?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="min-w-0 flex-1">
                      <p className={cn(
                        'text-[12px] font-medium truncate transition-colors',
                        isCurrent
                          ? 'text-purple-300 font-semibold'
                          : isActive
                            ? 'text-white'
                            : 'text-zinc-400 group-hover:text-zinc-200',
                      )}>
                        {project?.name}
                      </p>
                      <p className="text-[10px] text-zinc-600 truncate">
                        {project?.author?.username ? `@${project.author.username}` : ''}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />
                        <span className="text-[9px] font-medium" style={{ color: zone.color }}>{zone.label}</span>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Fade edges when there's overflow */}
          {trackOffset > 0 && (
            <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-zinc-800/60 to-transparent pointer-events-none z-10" />
          )}
          {trackOffset < chain.length - VISIBLE_CARDS && (
            <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-zinc-800/60 to-transparent pointer-events-none z-10" />
          )}
        </div>

        {/* Nav row */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-800/40">
          <button
            onClick={() => handleJump(activeIdx - 1)}
            disabled={activeIdx === 0}
            className={cn(
              'flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md transition-all',
              activeIdx === 0
                ? 'text-zinc-700 cursor-default'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50',
            )}
          >
            <ChevronLeft className="w-3 h-3" />
            Prev
          </button>

          <span className="text-[10px] text-zinc-600 tabular-nums select-none">
            {activeIdx + 1} / {chain.length}
          </span>

          <button
            onClick={() => handleJump(activeIdx + 1)}
            disabled={activeIdx === chain.length - 1}
            className={cn(
              'flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md transition-all',
              activeIdx === chain.length - 1
                ? 'text-zinc-700 cursor-default'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50',
            )}
          >
            Next
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================

const CollapsibleSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false, count }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-zinc-800/40 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 p-3.5 text-left hover:bg-zinc-800/30 transition-colors"
      >
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: iconColor || '#a1a1aa' }} />
        <span className="text-[13px] font-medium text-zinc-300 flex-1">{title}</span>
        {count != null && (
          <span className="text-[11px] text-zinc-600 tabular-nums">{count}</span>
        )}
        <ChevronDown
          className={cn('w-3.5 h-3.5 text-zinc-600 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 animate-in fade-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// COLLECTION CARD — self-contained so each card tracks its own expand state
// ============================================================================

const CollectionCard = ({ branch, onViewBranch }) => {
  const [descExpanded, setDescExpanded] = useState(false);

  const projectCount = branch.curatedCount || branch.curatedProjectIds?.length || 0;
  const views       = branch.viewCount || branch.view_count || 0;
  const isFeatured  = branch.isFeatured || branch.is_featured;
  const isPublic    = branch.isPublic ?? branch.is_public ?? false;
  const creatorName = branch.creator?.username;
  const creatorAvatar = branch.creator?.profilePictureUrl || branch.creator?.profile_picture_url;
  const creatorInitial = creatorName?.[0]?.toUpperCase() || '?';
  const rootName    = branch.rootProjectName || branch.root_project_name;
  const rootThumb   = branch.rootProjectThumbnail || branch.root_project_thumbnail;
  const description = branch.description;
  const createdAt   = branch.createdAt || branch.created_at;

  // Descriptions longer than ~120 chars get the expand toggle
  const CLAMP_THRESHOLD = 120;
  const isLong = description && description.length > CLAMP_THRESHOLD;

  return (
    <div
      className={cn(
        'w-full text-left rounded-xl p-3.5 transition-all group relative overflow-hidden',
        'bg-zinc-800/25 hover:bg-zinc-800/50 border',
        isFeatured
          ? 'border-purple-500/25 hover:border-purple-500/40'
          : 'border-zinc-800/50 hover:border-zinc-700/50',
      )}
    >
      {/* Featured gradient top accent */}
      {isFeatured && (
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-purple-500 to-fuchsia-500" />
      )}

      {/* Clickable body (navigate to collection) */}
      <button
        onClick={() => onViewBranch?.(branch)}
        className="w-full text-left active:scale-[0.98] transition-transform"
      >
        {/* Top row: avatar + title + badges */}
        <div className="flex items-start gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-[10px] flex-shrink-0 flex items-center justify-center text-xs font-semibold overflow-hidden"
            style={{
              backgroundColor: isFeatured ? 'rgba(168,85,247,0.15)' : 'rgba(113,113,122,0.15)',
              color: isFeatured ? '#c084fc' : '#a1a1aa',
            }}
          >
            {creatorAvatar ? (
              <img src={creatorAvatar} alt="" className="w-full h-full object-cover" />
            ) : (
              creatorInitial
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Name + badges */}
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[13px] font-semibold text-zinc-200 truncate group-hover:text-white transition-colors">
                {branch.name || `Collection #${branch.id}`}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                {isFeatured && (
                  <span className="flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/25">
                    <Sparkles className="w-2.5 h-2.5" />
                    FEATURED
                  </span>
                )}
                {isPublic ? (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    PUBLIC
                  </span>
                ) : (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-zinc-700/30 text-zinc-500 border border-zinc-700/30">
                    PRIVATE
                  </span>
                )}
              </div>
            </div>

            {/* Curator + time */}
            <p className="text-[11px] text-zinc-600">
              {creatorName && (
                <>by <span className="text-zinc-500 font-medium">@{creatorName}</span></>
              )}
              {createdAt && (
                <span className="ml-1">
                  {creatorName ? ' \u2022 ' : ''}
                  {timeAgo(createdAt)}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Root project indicator */}
        {rootName && (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-zinc-800/40 border border-zinc-800/60 mb-2.5">
            <div className="w-6 h-6 rounded-md flex-shrink-0 overflow-hidden flex items-center justify-center bg-purple-500/10">
              {rootThumb ? (
                <img src={rootThumb} alt="" className="w-full h-full object-cover" />
              ) : (
                <FolderTree className="w-3 h-3 text-purple-400/60" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-zinc-600 leading-none">Rooted from</p>
              <p className="text-[11px] text-zinc-400 font-medium truncate">{rootName}</p>
            </div>
          </div>
        )}

        {/* Stats footer */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[11px] text-zinc-600">
            <FolderTree className="w-3 h-3" />
            {projectCount} project{projectCount !== 1 ? 's' : ''}
          </span>
          {isPublic && views > 0 && (
            <span className="flex items-center gap-1.5 text-[11px] text-zinc-600">
              <Eye className="w-3 h-3" />
              {formatNumber(views)} view{views !== 1 ? 's' : ''}
            </span>
          )}
          {!isPublic && (
            <span className="flex items-center gap-1.5 text-[11px] text-zinc-600">
              <Lock className="w-3 h-3" />
              Private
            </span>
          )}
          <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-zinc-500 ml-auto transition-colors" />
        </div>
      </button>

      {/* Description — expandable, rendered outside the nav button so
          the "Show more" tap doesn't also navigate to the collection */}
      {description && (
        <div className="mt-2 mb-0.5">
          <p
            className="text-[12px] text-zinc-500 leading-relaxed"
            style={!descExpanded && isLong ? {
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            } : undefined}
          >
            {description}
          </p>
          {isLong && (
            <button
              onClick={e => { e.stopPropagation(); setDescExpanded(v => !v); }}
              className="mt-1 text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors font-medium"
            >
              {descExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// MAIN OVERLAY
// ============================================================================

export const ProjectDetailOverlay = ({
  project,
  parentProjects = [],
  childProjects = [],
  curatedBranches = [],
  onClose,
  onFork,
  onCreateChallenge,
  onEdit,
  onDelete,
  onViewProject,
  onNavigate,
  onViewBranch,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const overlayRef = useRef(null);
  const scrollRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showPurchaseOverlay, setShowPurchaseOverlay] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const [filesLoading, setFilesLoading] = useState(true);
  const [showCitations, setShowCitations] = useState(false);
  const [citationLineage, setCitationLineage] = useState([]);
  const [contentTransitioning, setContentTransitioning] = useState(false);
  const prevProjectIdRef = useRef(project?.id);

  // ── Presence state ──────────────────────────────────────────────────────
  const [isWorking, setIsWorking] = useState(
    Boolean(project?.is_working || project?.isWorking)
  );
  const [workingLoading, setWorkingLoading] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const heartbeatRef = useRef(null);

  // ── Crossfade on project navigation ──
  useEffect(() => {
    if (prevProjectIdRef.current === project?.id) return;
    prevProjectIdRef.current = project?.id;

    setContentTransitioning(true);

    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }

    setShowDeleteConfirm(false);
    setLinkCopied(false);
    setShowCitations(false);
    setShowPurchaseOverlay(false);

    const timer = setTimeout(() => setContentTransitioning(false), 80);
    return () => clearTimeout(timer);
  }, [project?.id]);

  const isOwner = user && (
    user.id === project.user_id || user.id === project.userId || user.id === project.author?.id
  );

  // ── Backlinks: one fetch feeds the description chips AND the panel ─────
  const { data: connections, patchMentionedBy } = useProjectConnections(project.id);

  // ── Presence: fetch initial state + heartbeat ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const [workRes, sessRes] = await Promise.all([
          api.get(`/presence/projects/${project.id}/working`).catch(() => null),
          api.get(`/presence/projects/${project.id}/session`).catch(() => null),
        ]);
        if (cancelled) return;
        if (workRes?.data) setIsWorking(Boolean(workRes.data.isWorking));
        if (sessRes?.data?.active) setLiveSession(sessRes.data.session);
      } catch { /* silent */ }
    };
    fetchPresence();
    return () => { cancelled = true; };
  }, [project.id]);

  // Keep session alive while owner has it open
  useEffect(() => {
    if (!liveSession || !isOwner) {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      return;
    }
    heartbeatRef.current = setInterval(async () => {
      try {
        await api.post(`/presence/projects/${project.id}/session/heartbeat`);
      } catch {
        setLiveSession(null);
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      }
    }, 30_000);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [liveSession, isOwner, project.id]);

  const handleToggleWorking = async () => {
    if (workingLoading) return;
    setWorkingLoading(true);
    try {
      const res = await api.post(`/presence/projects/${project.id}/working`);
      setIsWorking(Boolean(res.data.isWorking));
    } catch { /* silent */ }
    finally { setWorkingLoading(false); }
  };

  const handleToggleSession = async () => {
    if (liveLoading) return;
    setLiveLoading(true);
    try {
      if (liveSession) {
        await api.post(`/presence/projects/${project.id}/session/stop`);
        setLiveSession(null);
      } else {
        const res = await api.post(`/presence/projects/${project.id}/session/start`);
        setLiveSession(res.data.session);
      }
    } catch { /* silent */ }
    finally { setLiveLoading(false); }
  };

  const zone = getProjectZone(project);
  const projectType = PROJECT_TYPES[project.project_type || project.projectType || 'digital'];
  const tags = parseTags(project.tags);
  const TypeIcon = projectType.id === 'physical' ? Package : projectType.id === 'hybrid' ? Layers : Monitor;
  const imageUrl = project.image_url || project.imageUrl;
  const forks = project.fork_count || project.forkCount || 0;
  const views = project.view_count || project.viewCount || 0;
  const hasLineage = (project.parent_project_id || project.parent_project_ids?.length > 0 || forks > 0);

  // Fetch fresh project data + latest version for files section
  const [freshProject, setFreshProject] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setFilesLoading(true);
      try {
        const [projRes, verRes] = await Promise.all([
          api.get(`/projects/${project.id}`),
          api.get(`/projects/${project.id}/versions`).catch(() => ({ data: { versions: [] } }))
        ]);
        if (!cancelled) {
          setFreshProject(projRes.data.project || projRes.data);
          const versions = verRes.data.versions || [];
          if (versions.length > 0) setLatestVersion(versions[0]);
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setFilesLoading(false); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [project.id]);

  // Fetch lineage for citations
  useEffect(() => {
    if (!showCitations) return;
    let cancelled = false;
    const fetchLineage = async () => {
      try {
        const res = await api.get(`/citations/project/${project.id}`, { params: { format: 'all', include_lineage: 'true' } });
        if (!cancelled && res.data?.lineage?.chain) {
          setCitationLineage(res.data.lineage.chain.map(p => ({
            id: p.id, name: p.name,
            author: { username: p.author },
            created_at: p.created_at,
          })));
        }
      } catch {
        if (!cancelled) setCitationLineage([]);
      }
    };
    fetchLineage();
    return () => { cancelled = true; };
  }, [showCitations, project.id]);

  // ── Click outside + Escape ──────────────────────────────────────────────
  // IMPORTANT: ignore clicks that land inside a role="listbox" dropdown
  // (the MentionTextarea autocomplete). Those clicks use onMouseDown to
  // insert a token before the textarea blur fires. If we close the overlay
  // on that same mousedown the token never gets inserted.
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Let mention-autocomplete dropdown clicks through
      if (e.target.closest('[role="listbox"]')) return;
      if (overlayRef.current && !overlayRef.current.contains(e.target)) onClose?.();
    };
    const handleEscape = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/project/${project.id}`;
    const success = await copyToClipboard(url);
    if (success) { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }
  };

  // Build file list
  const versionFile = latestVersion ? {
    name: latestVersion.fileName || latestVersion.file_name,
    downloadUrl: latestVersion.fileUrl || latestVersion.file_url,
    fileType: latestVersion.fileType || latestVersion.file_type || 'File',
    fileSize: latestVersion.fileSize || latestVersion.file_size,
    isLatest: true,
    versionNumber: latestVersion.versionNumber || latestVersion.version_number,
  } : null;

  const projectFiles = collectProjectFiles(freshProject || project);
  const allFiles = versionFile ? [versionFile, ...projectFiles] : projectFiles;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overlay-backdrop">
      <div
        ref={overlayRef}
        className="relative w-full max-w-lg max-h-[92vh] bg-zinc-900 border border-zinc-800/80
                   rounded-3xl overflow-hidden flex flex-col animate-overlay-in"
        style={{
          boxShadow: `0 25px 60px -12px rgba(0,0,0,0.6), 0 0 80px -20px ${zone.color}20`,
          transition: 'box-shadow 0.4s ease',
        }}
      >
        {/* Close button - floats on top of everything */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 bg-zinc-900/60 hover:bg-zinc-800 backdrop-blur-xl
                     rounded-full transition-colors border border-white/[0.06]"
        >
          <X className="w-4 h-4 text-zinc-400" />
        </button>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#27272a transparent' }}
        >
          {/* Content crossfade wrapper */}
          <div
            style={{
              opacity: contentTransitioning ? 0 : 1,
              transform: contentTransitioning ? 'translateY(6px)' : 'translateY(0)',
              transition: 'opacity 0.15s ease, transform 0.2s ease',
            }}
          >

          {/* ── HERO IMAGE ── */}
          <div className="relative h-56 sm:h-72 overflow-hidden">
            {imageUrl ? (
              <>
                <img src={imageUrl} alt={project.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent" />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${zone.color}15 0%, #18181b 60%)` }}>
                <span className="text-8xl font-black select-none" style={{ color: zone.color, opacity: 0.06 }}>
                  {project.name?.[0]?.toUpperCase()}
                </span>
              </div>
            )}

            {/* Zone + type badges floating on image */}
            <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-xl"
                style={{ backgroundColor: `${zone.color}25`, color: zone.color, border: `1px solid ${zone.color}40` }}>
                {zone.shortLabel}
              </span>
              <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-zinc-900/60 backdrop-blur-xl border border-white/[0.08]"
                style={{ color: projectType.color }}>
                <TypeIcon className="w-3 h-3" />
                {projectType.label}
              </span>
              {project.is_for_sale && (
                <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 backdrop-blur-xl">
                  <DollarSign className="w-3 h-3" />
                  {parseFloat(project.price) > 0 ? `$${parseFloat(project.price).toFixed(2)}` : 'Free'}
                </span>
              )}
            </div>

            {/* Lineage badge */}
            {hasLineage && (
              <div className="absolute top-4 right-14 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 backdrop-blur-xl">
                <GitFork className="w-3.5 h-3.5" />
                {forks > 0 ? `${forks} fork${forks !== 1 ? 's' : ''}` : 'Has lineage'}
              </div>
            )}
          </div>

          {/* ── CONTENT ── */}
          <div className="px-6 pb-6 -mt-10 relative z-10">

            {/* Title */}
            <h1 className="text-2xl font-bold text-white leading-tight mb-2.5">
              {project.name}
            </h1>

            {/* Author + time + stats */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() => {
                  if (project.author?.id) {
                    onClose?.();
                    navigate(`/profile/${project.author.id}`);
                  }
                }}
                className="flex items-center gap-2 group"
              >
                <Avatar
                  src={project.author?.profile_picture_url || project.author?.profilePictureUrl}
                  alt={project.author?.username}
                  size="xs"
                />
                <span className="text-sm text-zinc-400">
                  by <span className="text-zinc-200 font-medium group-hover:text-purple-300 transition-colors underline-offset-2 group-hover:underline">@{project.author?.username || 'Unknown'}</span>
                </span>
              </button>
              <span className="text-xs text-zinc-600">
                {timeAgo(project.created_at || project.createdAt)}
              </span>
            </div>

            {/* Description — [[p:123]] tokens render as clickable chips */}
            {project.description && (
              <MentionText
                text={project.description}
                connections={connections}
                onViewProject={onViewProject}
                className="text-sm text-zinc-400 leading-relaxed mb-5 whitespace-pre-wrap"
              />
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-5">
                {tags.map(tag => {
                  const physStyle = getPhysicalTagStyle(tag);
                  return (
                    <span
                      key={tag}
                      className={cn(
                        'px-2.5 py-1 text-xs rounded-lg border',
                        physStyle || 'bg-zinc-800/80 text-zinc-400 border-zinc-700/50'
                      )}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            )}

            {project.location && (
              <div className="flex items-center gap-1.5 mb-5 text-sm text-zinc-500">
                <MapPin className="w-3.5 h-3.5" />
                {project.location}
              </div>
            )}

            {/* ── PHYSICAL ITEM AVAILABILITY BANNER ── */}
            {(() => {
              const pType = project.project_type || project.projectType || 'digital';
              const availTags = tags.filter(t => PHYSICAL_TAGS.availability.tags.includes(t));
              const condTags = tags.filter(t => PHYSICAL_TAGS.condition.tags.includes(t));
              const craftTags = tags.filter(t => PHYSICAL_TAGS.craft.tags.includes(t));
              if ((pType === 'physical' || pType === 'hybrid') && (availTags.length > 0 || condTags.length > 0 || craftTags.length > 0)) {
                return (
                  <div className="mb-5 p-4 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-2.5">
                      <Package className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-400">Physical item</span>
                    </div>
                    {availTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {availTags.map(t => {
                          const label = AVAILABILITY_LABELS[t];
                          return (
                            <span key={t} className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                              {label || t}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {condTags.map(t => (
                        <span key={t} className="px-2.5 py-1 text-xs rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/25">
                          Condition: {t}
                        </span>
                      ))}
                      {craftTags.map(t => (
                        <span key={t} className="px-2.5 py-1 text-xs rounded-lg bg-violet-500/15 text-violet-300 border border-violet-500/25">
                          {t}
                        </span>
                      ))}
                    </div>
                    {!isOwner && availTags.length > 0 && (
                      <button
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('openDirectMessage', {
                            detail: {
                              userId: project.user_id || project.userId || project.author?.id,
                              subject: 'Interested in: ' + project.name,
                              projectId: project.id,
                            }
                          }));
                        }}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                                   text-white bg-emerald-600 hover:bg-emerald-500 transition-all duration-200
                                   hover:scale-[1.01] active:scale-[0.98]"
                        style={{ boxShadow: '0 4px 20px -4px rgba(16,185,129,0.3)' }}
                      >
                        <MessageCircle className="w-4 h-4" />
                        I'm interested
                      </button>
                    )}
                  </div>
                );
              }
              return null;
            })()}

            {/* Stats row */}
            <div className="flex items-center gap-4 text-sm text-zinc-500 mb-5 pb-5 border-b border-zinc-800/60">
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" /> {formatNumber(views)} view{views !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1.5">
                <GitFork className="w-4 h-4" /> {forks} fork{forks !== 1 ? 's' : ''}
              </span>
              {hasLineage && (
                <span className="flex items-center gap-1.5 text-purple-400/70">
                  <Layers className="w-4 h-4" /> Has lineage
                </span>
              )}
            </div>

            {/* ── OWNER PRESENCE PANEL ── */}
            {isOwner && (
              <div className="mb-5 p-3.5 rounded-2xl bg-zinc-800/30 border border-zinc-800/60 space-y-2.5">
                {/* Working on it toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                      isWorking ? 'bg-amber-500/20' : 'bg-zinc-700/40'
                    }`}>
                      <Zap className={`w-3.5 h-3.5 transition-colors ${isWorking ? 'text-amber-400' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <p className={`text-[12px] font-semibold transition-colors ${isWorking ? 'text-amber-300' : 'text-zinc-400'}`}>
                        Working on it
                      </p>
                      <p className="text-[10px] text-zinc-600 leading-none">
                        {isWorking ? 'Your node pulses on the map' : 'Let others know you\'re active'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleWorking}
                    disabled={workingLoading}
                    className={`relative w-10 h-5.5 rounded-full transition-all duration-300 focus:outline-none ${
                      isWorking ? 'bg-amber-500' : 'bg-zinc-700'
                    } ${workingLoading ? 'opacity-50' : ''}`}
                    style={{ minWidth: 40, height: 22 }}
                    aria-label="Toggle working on it"
                  >
                    <span
                      className="absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform duration-300"
                      style={{
                        width: 18, height: 18,
                        transform: isWorking ? 'translateX(18px)' : 'translateX(0)',
                      }}
                    />
                  </button>
                </div>

                {/* Go Live button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                      liveSession ? 'bg-emerald-500/20' : 'bg-zinc-700/40'
                    }`}>
                      <Radio className={`w-3.5 h-3.5 transition-colors ${liveSession ? 'text-emerald-400' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <p className={`text-[12px] font-semibold transition-colors ${liveSession ? 'text-emerald-300' : 'text-zinc-400'}`}>
                        Creative Session
                      </p>
                      <p className="text-[10px] text-zinc-600 leading-none">
                        {liveSession
                          ? `${liveSession.observers ?? 0} observer${liveSession.observers !== 1 ? 's' : ''} watching`
                          : 'Go live — others can drop in'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleSession}
                    disabled={liveLoading}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-200 border ${
                      liveSession
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-red-500/15 hover:text-red-300 hover:border-red-500/30'
                        : 'bg-zinc-700/40 text-zinc-400 border-zinc-700/60 hover:bg-emerald-500/10 hover:text-emerald-300 hover:border-emerald-500/20'
                    } ${liveLoading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {liveSession ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Live — End
                      </>
                    ) : (
                      <>Go Live</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Non-owner live session observer badge */}
            {!isOwner && liveSession && (
              <div className="mb-5 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/20">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                <p className="text-[12px] text-emerald-300 font-medium">
                  Creator is live right now
                </p>
                {liveSession.observers > 0 && (
                  <span className="ml-auto flex items-center gap-1 text-[11px] text-emerald-400/70">
                    <Users className="w-3 h-3" />
                    {liveSession.observers}
                  </span>
                )}
              </div>
            )}

            {/* ── PRIMARY ACTIONS ── */}
            <div className="flex items-center gap-3 mb-6">
              {project.is_for_sale && !isOwner ? (
                <button
                  onClick={() => setShowPurchaseOverlay(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{ boxShadow: '0 8px 30px -4px rgba(16,185,129,0.35)' }}
                >
                  <ShoppingBag className="w-4 h-4" />
                  {parseFloat(project.price) > 0 ? `Buy $${parseFloat(project.price).toFixed(2)}` : 'Get Free'}
                </button>
              ) : (
                <button
                  onClick={() => onFork?.(project)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{ backgroundColor: zone.color, boxShadow: `0 8px 30px -4px ${zone.color}40` }}
                >
                  <GitFork className="w-4 h-4" />
                  Fork This
                </button>
              )}

              <button
                onClick={() => onCreateChallenge?.(project)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium
                           text-zinc-300 bg-zinc-800 border border-zinc-700 hover:border-zinc-600 transition-colors"
              >
                <Target className="w-4 h-4" />
                Challenge
              </button>
            </div>

            {/* ── SECONDARY ACTIONS ROW ── */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 bg-zinc-800/40 hover:bg-zinc-800/70 border border-zinc-800/40 transition-all"
              >
                {linkCopied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
                {linkCopied ? 'Copied!' : 'Share'}
              </button>
              <button
                onClick={() => setShowCitations(!showCitations)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all',
                  showCitations
                    ? 'text-purple-300 bg-purple-500/10 border-purple-500/30'
                    : 'text-zinc-400 hover:text-zinc-200 bg-zinc-800/40 hover:bg-zinc-800/70 border-zinc-800/40'
                )}
              >
                <BookOpen className="w-3.5 h-3.5" />
                Cite
              </button>
              {isOwner && (
                <>
                  <div className="flex-1" />
                  <button
                    onClick={() => onEdit?.(freshProject || project)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 bg-zinc-800/40 hover:bg-zinc-800/70 border border-zinc-800/40 transition-all"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-red-400 hover:text-red-300 bg-zinc-800/40 hover:bg-red-500/10 border border-zinc-800/40 hover:border-red-500/30 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </>
              )}
            </div>

            {/* ── CITATIONS PANEL ── */}
            {showCitations && (
              <div className="mb-6 p-4 bg-zinc-800/30 border border-zinc-700/40 rounded-xl">
                <CitationGenerator
                  project={project}
                  lineage={citationLineage}
                  showLineageCitations={citationLineage.length > 1}
                />
              </div>
            )}

            {/* ── FILES ── */}
            {!filesLoading && allFiles.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2.5">
                  <h3 className="text-[13px] font-semibold text-zinc-300 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-zinc-500" />
                    Files
                    {versionFile && (
                      <span className="text-[10px] font-normal text-zinc-600 ml-1">
                        v{versionFile.versionNumber}
                      </span>
                    )}
                  </h3>
                  {allFiles.length > 1 && (
                    <button
                      onClick={async () => {
                        for (const f of allFiles) {
                          triggerDownload(f.downloadUrl, f.name);
                          await new Promise(r => setTimeout(r, 500));
                        }
                      }}
                      className="text-[11px] text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Download all ({allFiles.length})
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {allFiles.slice(0, 5).map((file, idx) => (
                    <FileRow key={file.id || idx} file={file} />
                  ))}
                  {allFiles.length > 5 && (
                    <p className="text-[11px] text-zinc-600 text-center py-1">
                      +{allFiles.length - 5} more files
                    </p>
                  )}
                </div>
              </div>
            )}

            {filesLoading && (
              <div className="mb-6 flex items-center gap-2 text-[12px] text-zinc-600">
                <div className="w-3.5 h-3.5 border-2 border-zinc-700 border-t-purple-500 rounded-full animate-spin" />
                Loading files...
              </div>
            )}

            {/* ── CREATIVE LINEAGE TREE ── */}
            <LineageTree
              parentProjects={parentProjects}
              childProjects={childProjects}
              currentProject={project}
              onViewProject={onViewProject}
            />

            {/* ── USED IN ── */}
            <UsedInPanel
              project={freshProject || project}
              isOwner={isOwner}
              homeZoneId={zone.id}
              latestVersion={latestVersion}
            />

            {/* ── CONNECTED TO — backlinks, links, challenge answers ── */}
            <ConnectionsPanel
              connections={connections}
              homeZoneId={zone.id}
              onViewProject={onViewProject}
              onMentionHidden={patchMentionedBy}
            />

            {/* ── CURATED BRANCHES ── */}
            {curatedBranches.length > 0 && (
              <div className="mb-6">
                <h3 className="text-[13px] font-semibold text-zinc-300 mb-3 flex items-center gap-2">
                  <FolderTree className="w-3.5 h-3.5 text-purple-400" />
                  In collections
                  <span className="text-[10px] text-zinc-600 font-normal">
                    {curatedBranches.length}
                  </span>
                </h3>
                <div className="space-y-2">
                  {curatedBranches.map(branch => (
                    <CollectionCard
                      key={branch.id}
                      branch={branch}
                      onViewBranch={onViewBranch}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── SECONDARY SECTIONS ── */}
            <div className="space-y-2 mb-5">
              <CollapsibleSection title="Version History" icon={History} iconColor="#a78bfa">
                <VersionHistory projectId={project.id} isOwner={isOwner} />
              </CollapsibleSection>

              <CollapsibleSection title="Deadlines" icon={Timer} iconColor="#f59e0b">
                <DeadlineTab projectId={project.id} isOwner={isOwner} />
              </CollapsibleSection>
            </div>

            {/* ── LICENSE ── */}
            <div className="px-3.5 py-2.5 rounded-xl bg-zinc-800/20 border border-zinc-800/30">
              <p className="text-[11px] text-zinc-600 leading-relaxed">
                Licensed under <span className="text-zinc-500 font-medium">CC BY-SA 4.0</span> &mdash;
                free to share and adapt with proper attribution.
              </p>
            </div>
          </div>
          </div>{/* end crossfade wrapper */}
        </div>

        {/* ── DELETE CONFIRMATION ── */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-20 bg-zinc-900/97 backdrop-blur-sm flex items-center justify-center p-6 rounded-3xl">
            <div className="text-center max-w-xs">
              <div className="w-14 h-14 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1.5">Delete this project?</h3>
              <p className="text-[13px] text-zinc-500 mb-5 leading-relaxed">
                This can't be undone. All files, versions, and history will be permanently removed.
              </p>
              <div className="flex gap-2.5 justify-center">
                <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button variant="danger" onClick={() => { onDelete?.(project); onClose?.(); }}>Delete</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Purchase overlay */}
      {showPurchaseOverlay && (
        <PurchaseOverlay project={project} onClose={() => setShowPurchaseOverlay(false)} />
      )}
    </div>
  );
};

export default ProjectDetailOverlay;
